import React, { useEffect, useState } from "react";
import { BookOpen, IndianRupee, ListChecks, Lock } from "lucide-react";
import api from "../../lib/api";

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

export default function AdminGapsAuditPanel({ userId }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get(`/admin/gaps-audit/${userId}`)
      .then((res) => {
        if (!cancelled) setAudit(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setAudit(null);
          setError(err?.response?.data?.detail || "Could not load Gaps Audit");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const entries = audit?.entries || [];
  const summary = audit?.summary || { total_gaps: 0, total_cost: 0, learning_list: [] };

  return (
    <section
      data-testid="admin-gaps-audit-panel"
      className="mt-6 p-4 rounded-xl border border-zinc-800 bg-zinc-900/30"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            Gaps Audit
          </div>
          <div className="text-sm text-white mt-1">
            Member worksheet history and cumulative result
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.18em] text-amber-300 border border-amber-500/30 bg-amber-500/5 px-2 py-1 rounded-full">
          <Lock className="w-3 h-3" /> Read-only
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-6" data-testid="admin-gaps-audit-loading">
          Loading audit…
        </div>
      ) : error ? (
        <div className="text-sm text-rose-300 py-4" data-testid="admin-gaps-audit-error">
          {error}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <AdminMetric
              testid="admin-gaps-total"
              icon={ListChecks}
              label="Total gaps"
              value={summary.total_gaps || 0}
            />
            <AdminMetric
              testid="admin-gaps-cost"
              icon={IndianRupee}
              label="Total cost"
              value={inr.format(summary.total_cost || 0)}
              gold
            />
            <AdminMetric
              testid="admin-gaps-learning-count"
              icon={BookOpen}
              label="Learning items"
              value={(summary.learning_list || []).length}
            />
          </div>

          {entries.length === 0 ? (
            <div
              data-testid="admin-gaps-audit-empty"
              className="rounded-lg border border-dashed border-zinc-800 px-4 py-6 text-sm text-zinc-500 text-center"
            >
              This member has not calculated any audit gaps yet.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-zinc-900" data-testid="admin-audit-readonly-table">
              <table className="w-full min-w-[720px] border-collapse">
                <thead>
                  <tr className="bg-zinc-950/70 text-left">
                    <th className="px-3 py-2.5 text-[9px] uppercase tracking-[0.2em] text-zinc-500">Audit Gap</th>
                    <th className="px-3 py-2.5 text-[9px] uppercase tracking-[0.2em] text-zinc-500">Cost</th>
                    <th className="px-3 py-2.5 text-[9px] uppercase tracking-[0.2em] text-zinc-500">Learning</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-900">
                  {entries.map((entry) => (
                    <tr key={entry.id} data-testid={`admin-audit-entry-${entry.id}`}>
                      <td className="px-3 py-3 align-top">
                        <div className="flex items-start gap-2">
                          <Lock className="w-3.5 h-3.5 text-zinc-600 mt-0.5 shrink-0" />
                          <div>
                            <div className="text-sm text-zinc-300">{entry.gap}</div>
                            <div className="text-[10px] text-zinc-600 mt-1">
                              Recorded {entry.created_at ? new Date(entry.created_at).toLocaleString("en-IN") : "—"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top text-sm font-semibold text-amber-300 whitespace-nowrap">
                        {inr.format(entry.cost || 0)}
                      </td>
                      <td className="px-3 py-3 align-top text-sm text-zinc-300">{entry.learning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function AdminMetric({ testid, icon: Icon, label, value, gold }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-zinc-800 bg-zinc-950/50 px-3 py-3">
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.18em] text-zinc-500">
        <Icon className={`w-3.5 h-3.5 ${gold ? "text-amber-300" : "text-emerald-300"}`} />
        {label}
      </div>
      <div className={`font-display text-xl font-black tracking-tight mt-2 ${gold ? "text-amber-300" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
